import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Phone, PhoneOff } from "lucide-react";
import { toast } from "sonner";

// Global listener: per-user WebSocket + polling fallback for incoming call invites
function useIncomingInvite() {
  const [invite, setInvite] = useState(null);
  const check = useCallback(() => api.get("/calls/incoming").then(({ data }) => setInvite((cur) => (data.invite?.id === cur?.id ? cur : data.invite))).catch(() => {}), []);
  useEffect(() => {
    check();
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    let ws; let closed = false; let retry;
    const connect = async () => {
      if (closed) return;
      let token = "";
      try { token = (await api.get("/ws-token")).data.token; } catch {}
      if (closed) return;
      ws = new WebSocket(`${base}/api/ws/user?token=${encodeURIComponent(token)}`);
      ws.onmessage = (e) => {
        try {
          const p = JSON.parse(e.data);
          if (p.type === "call_invite") setInvite(p.invite);
          else if (p.type === "call_invite_update") setInvite((cur) => (cur?.id === p.id ? null : cur));
          else if (p.type === "banned") window.location.assign("/login");
        } catch {}
      };
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 4000); };
    };
    connect();
    const poll = setInterval(check, 5000);
    return () => { closed = true; clearInterval(poll); clearTimeout(retry); if (ws) ws.close(); };
  }, [check]);
  return [invite, setInvite];
}

function useRingtone(active) {
  useEffect(() => {
    if (!active) return;
    const audio = new Audio("/sounds/ringtone.wav");
    audio.loop = true;
    audio.volume = 0.7;
    audio.play().catch(() => {});
    if (navigator.vibrate) navigator.vibrate([400, 200, 400]);
    return () => { audio.pause(); audio.currentTime = 0; audio.removeAttribute("src"); audio.load(); };
  }, [active]);
}

export function IncomingCallModal() {
  const [invite, setInvite] = useIncomingInvite();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inThisCall = invite && location.pathname === `/call/${invite.match_id}`;
  const show = !!invite && !inThisCall;
  useRingtone(show);
  const expiryRef = useRef(null);

  useEffect(() => {
    if (!invite) return;
    const ms = new Date(invite.expires_at).getTime() - Date.now();
    expiryRef.current = setTimeout(() => { setInvite(null); toast(`Missed vibe check from ${invite.caller?.name}`); }, Math.max(0, ms));
    return () => clearTimeout(expiryRef.current);
  }, [invite, setInvite]);

  if (!show) return null;
  const answer = async (accept) => {
    setBusy(true);
    const inv = invite;
    setInvite(null);
    try {
      await api.post(`/calls/invites/${inv.id}/${accept ? "accept" : "decline"}`);
      if (accept) navigate(`/call/${inv.match_id}?autostart=1${inv.bonus ? "&bonus=1" : ""}`);
      else navigate("/discover");
    } catch (e) {
      toast.error(e.response?.data?.detail || "This call is no longer available");
    } finally { setBusy(false); }
  };

  const c = invite.caller || {};
  return (
    <div data-testid="incoming-call-modal" className="fixed inset-0 z-[100] flex flex-col items-center justify-between text-center px-6 overflow-hidden"
      style={{ paddingTop: "max(4rem, env(safe-area-inset-top))", paddingBottom: "max(3rem, env(safe-area-inset-bottom))" }}>
      <img src={imgUrl(c.photo)} alt="" className="absolute inset-0 h-full w-full object-cover blur-3xl scale-125 opacity-50" />
      <div className="absolute inset-0 bg-[#050a07]/80 backdrop-blur-xl" />
      <div className="relative">
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300/80">MoYo Stars Date</p>
        {invite.event_title && <p className="text-sm text-slate-300 mt-1">{invite.event_title}</p>}
      </div>
      <div className="relative flex flex-col items-center">
        <div className="relative h-48 w-48 sm:h-56 sm:w-56">
          <span className="absolute inset-0 rounded-full border-2 border-emerald-400/60 animate-ping" />
          <span className="absolute -inset-4 rounded-full border border-emerald-300/30 animate-pulse" />
          <img data-testid="incoming-call-photo" src={imgUrl(c.photo)} alt={c.name} className="relative h-full w-full rounded-full object-cover ring-4 ring-emerald-400/70 shadow-[0_0_60px_rgba(52,211,153,0.35)]" />
        </div>
        <h2 data-testid="incoming-call-name" className="font-display font-black text-4xl mt-8 flex items-center gap-2">{c.name} {c.verified && <VerifiedBadge />}</h2>
        <p data-testid="incoming-call-subtitle" className="text-emerald-200 mt-2 animate-pulse">Incoming 60-sec Vibe Check...</p>
      </div>
      <div className="relative flex items-center justify-center gap-20 sm:gap-28">
        <div className="flex flex-col items-center gap-2">
          <button data-testid="incoming-call-decline" disabled={busy} onClick={() => answer(false)} aria-label="Decline"
            className="h-20 w-20 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-[0_10px_40px_rgba(239,68,68,0.5)] transition-transform active:scale-90">
            <PhoneOff className="h-9 w-9" />
          </button>
          <span className="text-sm text-slate-300">Decline</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <button data-testid="incoming-call-accept" disabled={busy} onClick={() => answer(true)} aria-label="Accept"
            className="h-20 w-20 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white flex items-center justify-center shadow-[0_10px_40px_rgba(16,185,129,0.55)] transition-transform active:scale-90 animate-bounce">
            <Phone className="h-9 w-9" />
          </button>
          <span className="text-sm text-slate-300">Accept</span>
        </div>
      </div>
    </div>
  );
}
